import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { CONTACT, HERO_IMAGE } from "../../lib/siteData";

const lines = ["We turn unusable", "land into usable", "property."];
const motes = Array.from({ length: 14 });

export const Hero = () => {
  const sectionRef = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const tx = useTransform(sx, [-0.5, 0.5], [26, -26]);
  const ty = useTransform(sy, [-0.5, 0.5], [18, -18]);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const scrollY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const scrollScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const onMove = (e) => {
    const r = sectionRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      onMouseMove={onMove}
      className="relative min-h-screen w-full overflow-hidden bg-[#080D0A]"
      data-testid="hero-section"
    >
      {/* Parallax photo background */}
      <div className="absolute inset-0 overflow-hidden" data-testid="hero-3d-canvas">
        <motion.div style={{ y: scrollY, scale: scrollScale }} className="absolute inset-0">
          <motion.div style={{ x: tx, y: ty }} className="absolute -inset-[7%]">
            <div
              className="w-full h-full bg-cover bg-center"
              style={{ backgroundImage: `url(${HERO_IMAGE})`, filter: "brightness(0.6) saturate(1.05)" }}
            />
          </motion.div>
        </motion.div>

        {/* floating warm motes */}
        {motes.map((_, i) => {
          const left = (i * 37) % 100;
          const top = ((i * 53) % 80) + 8;
          const dur = 6 + (i % 5);
          const size = 3 + (i % 4);
          return (
            <motion.span
              key={i}
              className="absolute rounded-full"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: size,
                height: size,
                background: "rgba(255,238,196,0.85)",
                boxShadow: "0 0 10px 2px rgba(255,220,150,0.6)",
              }}
              animate={{ y: [0, -22, 0], opacity: [0.2, 0.8, 0.2] }}
              transition={{ duration: dur, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
            />
          );
        })}

        {/* readability scrims */}
        <div className="absolute inset-0" style={{ background: "rgba(6,11,8,0.2)" }} />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(6,11,8,0.92) 0%, rgba(6,11,8,0.8) 45%, rgba(6,11,8,0.55) 62%, rgba(6,11,8,0.2) 78%, rgba(6,11,8,0) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(0deg, rgba(6,11,8,0.75) 0%, rgba(6,11,8,0.15) 42%, rgba(6,11,8,0) 64%)",
          }}
        />
        <div
          className="absolute top-0 left-0 right-0 h-32"
          style={{ background: "linear-gradient(180deg, rgba(6,11,8,0.6), transparent)" }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 min-h-screen flex flex-col justify-center pt-24 pb-16">
        <div className="hero-up flex items-center gap-3 mb-8" style={{ animationDelay: "0.1s" }}>
          <span className="text-xs uppercase tracking-[0.25em] text-lime-400 font-bold">
            Land Clearing & Excavation
          </span>
          <span className="h-px w-16 bg-white/30" />
          <span className="inline-flex items-center gap-1.5 text-xs text-neutral-100">
            <MapPin size={13} className="text-lime-400" /> {CONTACT.location}
          </span>
        </div>

        <h1
          className="font-display font-black uppercase tracking-tighter leading-[0.92] text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-5xl"
          style={{ textShadow: "0 2px 30px rgba(0,0,0,0.85), 0 1px 4px rgba(0,0,0,0.8)" }}
          data-testid="hero-heading"
        >
          {lines.map((line, i) => (
            <span key={i} className="block hero-up" style={{ animationDelay: `${0.25 + i * 0.12}s` }}>
              {i === 2 ? (
                <span>
                  property<span className="text-lime-400">.</span>
                </span>
              ) : (
                line
              )}
            </span>
          ))}
        </h1>

        <p
          className="hero-up mt-10 max-w-xl text-base md:text-lg text-neutral-100 leading-relaxed"
          style={{ animationDelay: "0.7s", textShadow: "0 1px 12px rgba(0,0,0,0.9)" }}
        >
          Owner-operated by {CONTACT.owner}. Quality land clearing, grading and
          dirt work done right — at a fair price, right here in {CONTACT.location}.
        </p>

        <div className="hero-up mt-10 flex flex-wrap items-center gap-4" style={{ animationDelay: "0.85s" }}>
          <a
            href="#estimate"
            data-testid="hero-estimate-btn"
            className="group relative overflow-hidden bg-lime-400 text-black font-semibold px-8 py-4 text-sm uppercase tracking-wider"
          >
            <span className="relative z-10">Get a Free Estimate</span>
            <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            <span className="absolute inset-0 z-10 flex items-center justify-center px-8 font-semibold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              Get a Free Estimate
            </span>
          </a>
          <a
            href="#work"
            data-testid="hero-work-btn"
            className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 text-sm uppercase tracking-wider hover:border-lime-400 hover:text-lime-400 transition-colors duration-200 backdrop-blur-sm"
          >
            See the Work
          </a>
        </div>
      </div>

      <div
        className="hero-up absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-neutral-200"
        style={{ animationDelay: "1.1s" }}
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce text-lime-400" />
      </div>
    </section>
  );
};
